import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from '../lib/auth';
import { loadGA4IfAllowed, unloadGA4 } from '../lib/analytics';
import { initSentryIfAllowed } from '../../instrument';
import type { CookiePreferences } from '../lib/cookie-consent';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: location.pathname });
    }
  }, [location.pathname]);
}

function useConsentListener() {
  useEffect(() => {
    // Load on mount if consent already exists
    loadGA4IfAllowed();
    initSentryIfAllowed();

    function handleConsentChange(e: Event) {
      const prefs = (e as CustomEvent<CookiePreferences>).detail;
      if (prefs.analytics) {
        loadGA4IfAllowed();
      } else {
        unloadGA4();
      }
      if (prefs.errorTracking) {
        initSentryIfAllowed();
      }
    }
    window.addEventListener('cookie-consent-change', handleConsentChange);
    return () => window.removeEventListener('cookie-consent-change', handleConsentChange);
  }, []);
}

export default function AuthRoot() {
  usePageTracking();
  useConsentListener();
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
